/**
 * Dashboard "Meal" card — surfaces the latest analysis's Resume Honesty
 * Check score and deep-links to the full audit panel on the dashboard
 * result page.
 *
 * Loads `/api/integrity/{analysis_id}` for the latest makeover (if any).
 * Three visual states:
 *   - Locked: no makeover yet → "Tailor a JD first."
 *   - Pending: makeover exists but check absent/error → CTA to open the result page
 *   - Complete: shows the score ring + flag-count chips + "Review →" CTA
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import AuthenticitySeal from "../AuthenticitySeal";

function tone(score) {
  if (score == null) return { color: "#94A3B8", soft: "#F1F5F9", label: "—" };
  if (score >= 90)   return { color: "#0E5A4A", soft: "#CFFAFE", label: "Honest" };
  if (score >= 75)   return { color: "#047857", soft: "#D1FAE5", label: "Mostly honest" };
  if (score >= 55)   return { color: "#CA8A04", soft: "#FEF3C7", label: "Some embellishment" };
  return                       { color: "#B91C1C", soft: "#FEE2E2", label: "Needs review" };
}

export default function IntegrityCard({ makeover }) {
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  const analysisId = makeover?.analysis_id;
  const locked = !analysisId;

  useEffect(() => {
    if (!analysisId) return undefined;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/integrity/${analysisId}`);
        if (alive) setCheck(data);
      } catch { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [analysisId]);

  const onClick = () => {
    if (locked) return;
    // Deep-link to the analysis result page where the IntegrityPanel lives.
    navigate(`/dashboard/${analysisId}#integrity`);
  };

  const score = check?.integrity_score;
  const status = check?.status;
  const flags = check?.flags || [];
  const t = tone(score);
  const reds    = flags.filter((f) => f.severity === "red");
  const yellows = flags.filter((f) => f.severity === "yellow");
  const pending = flags.filter((f) => f.status === "pending");
  // Verified = user resolved every flag and the audit settled at 100/100.
  // Drives the swap from the generic ShieldCheck icon to the gold
  // Authenticity Seal in the leading slot — same gate as History/Studio.
  const verified =
    status === "complete" && flags.length > 0 && pending.length === 0 && (score ?? 0) >= 100;

  return (
    <div
      className="rm-card-static p-4 md:p-5"
      style={{
        background: "white",
        borderColor: "#0F1B2D",
        boxShadow: locked ? "4px 4px 0 0 #94A3B8" : `6px 6px 0 0 ${t.color}`,
        opacity: locked ? 0.7 : 1,
      }}
      data-testid="integrity-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        {verified ? (
          <div
            className="flex-shrink-0 flex items-center justify-center"
            style={{ width: 56, height: 56, lineHeight: 0 }}
            data-testid="integrity-card-seal"
          >
            <AuthenticitySeal
              verified
              size={56}
              title="Resmo Certified · This resume passed the Honesty Check at 100/100."
              testId="integrity-card-seal-img"
            />
          </div>
        ) : (
          <div
            className="flex-shrink-0 flex items-center justify-center"
            style={{ width: 44, height: 44, borderRadius: 10, background: t.soft, color: t.color }}
          >
            <ShieldCheck size={20} strokeWidth={2.4} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-0.5"
               style={{ color: t.color, fontWeight: 700 }}>
            · Resume honesty check
          </div>
          <div className="font-display uppercase leading-tight"
               style={{ fontSize: "1.15rem", letterSpacing: "-0.02em", color: "#0F1B2D" }}>
            {locked
              ? "Run a tailored resume first."
              : status === "running"
                ? "Audit in progress…"
                : status === "error"
                  ? "Audit hit an issue."
                  : score != null
                    ? `${score}/100 · ${t.label}`
                    : loading
                      ? "Loading…"
                      : "Audit not run yet."}
          </div>
          <p className="text-[12.5px] mt-1.5 leading-snug" style={{ color: "#475569" }}>
            {locked
              ? "Once you tailor a resume to a Job Description, Resmo will automatically check for any claims that drift from your original — and let you approve, replace, or rewrite them."
              : status === "error"
                ? "The audit didn't finish. Open the resume to retry."
                : flags.length > 0
                  ? `Resmo flagged ${flags.length} claim${flags.length === 1 ? "" : "s"} for your review.`
                  : status === "complete"
                    ? "Every claim traces back to your original. Solid."
                    : "Open the resume to see deviations + grounded alternatives."}
          </p>
        </div>
      </div>

      {/* Flag breakdown chips */}
      {!locked && flags.length > 0 && (
        <div className="flex items-center gap-2 mt-3 flex-wrap"
             data-testid="integrity-card-flags">
          {reds.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold"
                  style={{ background: "#FEE2E2", color: "#B91C1C", border: "2px solid #B91C1C", borderRadius: 999 }}>
              <AlertTriangle size={11} /> {reds.length} red
            </span>
          )}
          {yellows.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold"
                  style={{ background: "#FEF3C7", color: "#CA8A04", border: "2px solid #CA8A04", borderRadius: 999 }}>
              <AlertCircle size={11} /> {yellows.length} yellow
            </span>
          )}
          {pending.length > 0 && (
            <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#475569" }}>
              · {pending.length} pending review
            </span>
          )}
          {pending.length === 0 && flags.length > 0 && (
            <span className="inline-flex items-center gap-1 font-mono-rm uppercase text-[10px] tracking-widest"
                  style={{ color: "#047857" }}>
              <CheckCircle2 size={11} /> All resolved
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onClick}
        disabled={locked}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5 mt-3"
        style={{
          background: locked ? "#E5E7EB" : t.color,
          color: locked ? "#94A3B8" : "white",
          border: "none",
          borderRadius: 8,
          cursor: locked ? "not-allowed" : "pointer",
          boxShadow: locked ? "none" : `0 4px 12px ${t.color}33`,
        }}
        data-testid="integrity-card-cta"
      >
        {locked
          ? "Locked · tailor a resume first"
          : flags.length > 0
            ? `Review ${flags.length} flag${flags.length === 1 ? "" : "s"} →`
            : status === "complete"
              ? "View the honesty audit →"
              : "Open the resume →"}
        {!locked && <ArrowRight size={13} />}
      </button>
    </div>
  );
}
